import { useEffect, useRef, useState } from 'react';
import { formatDateShort, formatWeekday, todayIso } from '../../lib/dates';
import { ABSENCE_COLOR, ABSENCE_LABELS, ABSENCE_SHORT, absenceReason } from '../../lib/labels';
import { describeTile, type DayColumn, type PlanEntry, type PlanModel, type SiteTile } from './planModel';
import type { Employee } from '../../lib/types';

/** Kennungen fürs Ziehen — eine je Herkunftsart. */
const DRAG_ASSIGNMENT = 'application/x-egger-assignment';
const DRAG_EMPLOYEE = 'application/x-egger-employee';

/**
 * Was gerade zum Umsetzen angetippt ist.
 *
 * Ziehen mit der Maus funktioniert auf dem Handy nicht — dort wird stattdessen
 * angetippt: erst der Name, dann die Ziel-Kachel. Derselbe Weg funktioniert am
 * Rechner ebenfalls, deshalb gibt es keine zweite Bedienlogik nur für Touch.
 */
export type Picked =
  | { kind: 'einteilung'; assignmentId: number; employee: Employee; siteId: number; date: string }
  | { kind: 'mitarbeiter'; employee: Employee; date: string };

interface PlanBoardProps {
  model: PlanModel;
  canEdit: boolean;
  picked: Picked | null;
  onPick: (picked: Picked | null) => void;
  onOpenTile: (siteId: number, date: string) => void;
  onAddSite: (date: string) => void;
  onEditTile: (tile: SiteTile, date: string) => void;
  onRemoveTile: (tile: SiteTile, date: string) => void;
  onAssign: (employeeId: number, siteId: number, date: string) => void;
  onMove: (assignmentId: number, siteId: number, date: string) => void;
  onUnassign: (assignmentId: number) => void;
}

/**
 * Der Plan als Tagesbrett: eine Spalte je Tag, darin je Baustelle eine Kachel
 * mit den Namen. Am Fuß jedes Tages steht, wer noch offen und wer abwesend ist
 * — die Frage, die beim Planen am häufigsten aufkommt.
 */
export function PlanBoard(props: PlanBoardProps) {
  const today = todayIso();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Beim Öffnen zum heutigen Tag rollen — vor allem auf dem Handy, wo nur
  // ein Tag ins Bild passt, erspart das jeden Morgen das Wischen ab Montag.
  const datesKey = props.model.columns.map((column) => column.date).join('|');
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || box.scrollWidth <= box.clientWidth) return;
    const todayColumn = box.querySelector<HTMLElement>('.plan-day.is-today');
    if (todayColumn) box.scrollLeft = todayColumn.offsetLeft;
    // eslint-disable-next-line react-hooks/exhaustive-deps — nur bei neuem Zeitraum, nicht bei jeder Einteilung
  }, [datesKey]);

  return (
    <div className="plan-scroll" ref={scrollRef}>
      <div className="plan-board" role="list" aria-label="Schichtplan nach Tagen">
        {props.model.columns.map((column) => (
          <DayView key={column.date} column={column} today={today} {...props} />
        ))}
      </div>
    </div>
  );
}

interface DayViewProps extends PlanBoardProps {
  column: DayColumn;
  today: string;
}

function DayView({ column, today, ...props }: DayViewProps) {
  const { canEdit, picked, onAddSite } = props;
  const open = column.unplanned.length;

  return (
    <section
      className={classes(
        'plan-day',
        column.weekend && 'is-weekend',
        column.holiday && 'is-holiday',
        column.date === today && 'is-today',
      )}
      role="listitem"
      aria-label={`${formatWeekday(column.date)}, ${formatDateShort(column.date)}`}
    >
      <header className="plan-day-head">
        <span className="plan-day-weekday">{formatWeekday(column.date)}</span>
        <span className="plan-day-date numeric">{formatDateShort(column.date)}</span>
        {column.holiday ? (
          <span className="plan-day-holiday">{column.holiday}</span>
        ) : (
          <span className="plan-day-tally numeric" title={`${column.staffed} eingeteilt, ${open} offen`}>
            {column.staffed} eingeteilt
            {open > 0 ? <em>{open} offen</em> : null}
          </span>
        )}
      </header>

      <div className="plan-day-body">
        {column.tiles.map((tile) => (
          <TileView key={tile.site.id} tile={tile} date={column.date} {...props} />
        ))}

        {column.tiles.length === 0 ? (
          <p className="plan-day-empty">Für diesen Tag ist noch keine Baustelle eingeplant.</p>
        ) : null}

        {canEdit ? (
          <button
            type="button"
            className="plan-day-add"
            onClick={() => onAddSite(column.date)}
            disabled={Boolean(picked)}
          >
            + Baustelle
          </button>
        ) : null}

        <RestBlock column={column} {...props} />
      </div>
    </section>
  );
}

interface TileViewProps extends PlanBoardProps {
  tile: SiteTile;
  date: string;
}

/** Eine Baustelle an einem Tag — die Kachel, in die eingeteilt wird. */
function TileView({ tile, date, ...props }: TileViewProps) {
  const { canEdit, picked, onPick, onOpenTile, onEditTile, onRemoveTile, onAssign, onMove, onUnassign } =
    props;
  const [isDropTarget, setDropTarget] = useState(false);

  const drop = (employeeId: number | null, assignmentId: number | null) => {
    if (assignmentId !== null) onMove(assignmentId, tile.site.id, date);
    else if (employeeId !== null) onAssign(employeeId, tile.site.id, date);
  };

  /** Der angetippte Name landet hier. */
  const takePicked = () => {
    if (!picked) return;
    if (picked.kind === 'einteilung') {
      if (picked.siteId === tile.site.id && picked.date === date) onPick(null);
      else drop(null, picked.assignmentId);
    } else {
      drop(picked.employee.id, null);
    }
    onPick(null);
  };

  const targetable = canEdit && Boolean(picked);

  return (
    <article
      className={classes(
        'plan-tile',
        tile.site.permanent && 'is-permanent',
        isDropTarget && 'is-drop',
        targetable && 'is-targetable',
        tile.entries.length === 0 && 'is-empty',
      )}
      style={{ '--site': tile.site.color } as never}
      aria-label={describeTile(tile, date)}
      onDragOver={(event) => {
        if (!canEdit) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTarget(true);
      }}
      onDragLeave={() => setDropTarget(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDropTarget(false);
        if (!canEdit) return;
        const assignmentId = event.dataTransfer.getData(DRAG_ASSIGNMENT);
        const employeeId = event.dataTransfer.getData(DRAG_EMPLOYEE);
        drop(employeeId ? Number(employeeId) : null, assignmentId ? Number(assignmentId) : null);
      }}
    >
      <header className="plan-tile-head">
        <button
          type="button"
          className="plan-tile-open"
          aria-label={`${tile.site.name} — einteilen`}
          onClick={() => onOpenTile(tile.site.id, date)}
          disabled={targetable}
        >
          <span className="plan-tile-name">{tile.site.name}</span>
        </button>
        <span className="plan-tile-count numeric" aria-label={`${tile.entries.length} eingeteilt`}>
          {tile.entries.length}
        </span>
        {canEdit ? (
          <button
            type="button"
            className="plan-tile-x plan-tile-edit"
            aria-label={`${tile.site.name} an diesem Tag bearbeiten`}
            title="Info für diesen Tag eintragen"
            onClick={() => onEditTile(tile, date)}
            disabled={targetable}
          >
            ✎
          </button>
        ) : null}
        {/* Feste Bereiche stehen immer im Plan — sie lassen sich nicht vom Tag nehmen. */}
        {canEdit && !tile.site.permanent ? (
          <button
            type="button"
            className="plan-tile-x"
            aria-label={`${tile.site.name} von diesem Tag nehmen`}
            title="Baustelle von diesem Tag nehmen"
            onClick={() => onRemoveTile(tile, date)}
            disabled={targetable}
          >
            ✕
          </button>
        ) : null}
      </header>

      {tile.note ? <p className="plan-tile-info">{tile.note}</p> : null}

      <div className="plan-tile-names">
        {tile.groups.map((group) => {
          const chips = group.entries.map((entry) => (
            <NameChip
              key={entry.assignment.id}
              entry={entry}
              date={date}
              siteId={tile.site.id}
              canEdit={canEdit}
              picked={picked}
              onPick={onPick}
              onRemove={() => onUnassign(entry.assignment.id)}
            />
          ));

          // Eine Kolonne steht als eingerahmte Einheit in der Kachel — ihre
          // Namen bleiben einzeln bedienbar. Lose Namen stehen wie bisher.
          return group.crew ? (
            <div
              key={`kolonne-${group.crew.id}`}
              className="plan-crew"
              style={{ '--crew': group.crew.color } as never}
            >
              <span className="plan-crew-name">{group.crew.name}</span>
              <div className="plan-crew-names">{chips}</div>
            </div>
          ) : (
            chips
          );
        })}
        {tile.entries.length === 0 ? (
          <span className="plan-tile-hint">niemand eingeteilt</span>
        ) : null}
      </div>

      {canEdit && !targetable ? (
        <button
          type="button"
          className="plan-tile-add"
          onClick={() => onOpenTile(tile.site.id, date)}
        >
          + Mitarbeiter
        </button>
      ) : null}

      {targetable ? (
        <button type="button" className="plan-tile-target" onClick={takePicked}>
          Hierher
        </button>
      ) : null}
    </article>
  );
}

interface NameChipProps {
  entry: PlanEntry;
  date: string;
  siteId: number;
  canEdit: boolean;
  picked: Picked | null;
  onPick: (picked: Picked | null) => void;
  onRemove: () => void;
}

/** Ein Name in einer Kachel. Ziehbar am Rechner, antippbar auf dem Handy. */
function NameChip({ entry, date, siteId, canEdit, picked, onPick, onRemove }: NameChipProps) {
  const { conflict, employee } = entry;
  const isPicked = picked?.kind === 'einteilung' && picked.assignmentId === entry.assignment.id;

  const title = conflict
    ? `${employee.displayName} — eingeteilt, obwohl nicht verfügbar: ${absenceReason(conflict)}`
    : `${employee.displayName}${entry.assignment.note ? ` — ${entry.assignment.note}` : ''}`;

  return (
    <span
      className={classes('plan-chip', conflict && 'has-conflict', isPicked && 'is-picked')}
      draggable={canEdit}
      onDragStart={(event) => {
        event.dataTransfer.setData(DRAG_ASSIGNMENT, String(entry.assignment.id));
        event.dataTransfer.effectAllowed = 'move';
      }}
      title={title}
    >
      {conflict ? (
        <span className="plan-conflict" aria-hidden="true">
          !
        </span>
      ) : null}
      <button
        type="button"
        className="plan-chip-name"
        disabled={!canEdit}
        aria-pressed={isPicked}
        onClick={() =>
          onPick(
            isPicked
              ? null
              : {
                  kind: 'einteilung',
                  assignmentId: entry.assignment.id,
                  employee,
                  siteId,
                  date,
                },
          )
        }
      >
        {employee.displayName}
      </button>
      {canEdit ? (
        <button
          type="button"
          className="plan-chip-x"
          aria-label={`${employee.displayName} austragen`}
          onClick={onRemove}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/** Fuß eines Tages: wer noch offen ist und wer fehlt. */
function RestBlock({ column, canEdit, picked, onPick }: PlanBoardProps & { column: DayColumn }) {
  return (
    <div className="plan-rest">
      <h4 className="plan-rest-head">
        Nicht eingeteilt <span className="numeric">{column.unplanned.length}</span>
      </h4>
      <div className="plan-rest-names">
        {column.unplanned.map((employee) => {
          const isPicked =
            picked?.kind === 'mitarbeiter' &&
            picked.employee.id === employee.id &&
            picked.date === column.date;

          return (
            <span
              key={employee.id}
              className={classes('plan-chip', 'is-open', isPicked && 'is-picked')}
              draggable={canEdit}
              onDragStart={(event) => {
                event.dataTransfer.setData(DRAG_EMPLOYEE, String(employee.id));
                event.dataTransfer.effectAllowed = 'copy';
              }}
            >
              <button
                type="button"
                className="plan-chip-name"
                disabled={!canEdit}
                aria-pressed={isPicked}
                title={
                  canEdit
                    ? `${employee.displayName} — antippen, dann die Baustelle wählen`
                    : employee.displayName
                }
                onClick={() =>
                  onPick(isPicked ? null : { kind: 'mitarbeiter', employee, date: column.date })
                }
              >
                {employee.displayName}
              </button>
            </span>
          );
        })}
        {column.unplanned.length === 0 ? <span className="plan-rest-none">alle verplant</span> : null}
      </div>

      {column.absent.length > 0 ? (
        <>
          <h4 className="plan-rest-head">
            Abwesend <span className="numeric">{column.absent.length}</span>
          </h4>
          <div className="plan-rest-names">
            {column.absent.map(({ employee, absence }) => (
              <span
                key={employee.id}
                className="plan-chip is-absent"
                style={{ '--abs': ABSENCE_COLOR[absence.type] } as never}
                title={`${employee.displayName} — nicht verfügbar: ${absenceReason(absence)}`}
              >
                {employee.displayName}
                <em title={ABSENCE_LABELS[absence.type]}>{ABSENCE_SHORT[absence.type]}</em>
              </span>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function classes(...values: (string | false | null | undefined)[]): string {
  return values.filter(Boolean).join(' ');
}
